<template>
  <div
    class="insights-work-time-expenditure-widget insights-widget"
  >
    <InsightsWorkTimeExpenditureDialog
      v-if="showDialog && hasSelectedData"
      title="Work Time Expenditure"
      :show="showDialog"
      :widget-key="widgetKey"
      :sections="chartSections"
      :chart-data="chartData"
      @close="toggleDialog"
    />
    <div class="widget-wrapper">
      <div class="widget-common widget-small widget-full-height">
        <div class="widget-title d-flex align-items-center justify-content-between">
          <div>
            {{ title }}
            <i
              v-if="tooltip"
              v-tooltip="tooltipOptions"
              :title="tooltip"
              class="hi hi-info-filled ml-5"
            />
          </div>
          <a
            v-if="showMore"
            v-track:click="trackDetailsProperties"
            class="insights-widget-view-more"
            @click.prevent="toggleDialog"
          >
            <i class="hi hi-external" />
            <span>View all</span>
          </a>
        </div>
        <div
          class="widget-body d-flex"
          :class="bodyClasses"
        >
          <template v-if="isReady">
            <template v-if="!isEmptyWidget">
              <AppChartStacked
                :chart-id="`${widgetKey}-vertical`"
                class="d-flex align-items-end"
                :sections="chartSections"
                :chart-data="chartData"
                custom-class="insights-work-time-expenditure-widget-popover"
                dynamic-height
                with-tooltips
                show-popovers
                popover-origin-position="column"
                popover-trigger="click"
              >
                <template #popover-content="{ columnData }">
                  <InsightsWorkTimeExpenditurePopover :column-data="columnData" />
                </template>
              </AppChartStacked>
            </template>
            <InsightsNoData
              v-else
              :type="emptyReason"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { INSIGHTS } from '~/app_constants';
import { Insights, InsightsTracking } from '~/mixins';
import { mapActions, mapGetters, mapState } from 'vuex';
import AppChartStacked from '~/components/AppChartStacked.vue';
import InsightsWorkTimeExpenditureDialog from '~/components/insights/InsightsWorkTimeExpenditureDialog.vue';
import InsightsWorkTimeExpenditurePopover from '~/components/insights/InsightsWorkTimeExpenditurePopover.vue';

const formatFields = (item) => ({
  non_core: {
    value: item.non_core,
  },
  core: {
    value: item.core,
  },
  unproductive: {
    value: item.unproductive,
  },
});

export default {
  name: 'InsightsWorkTimeExpenditureWidget',
  components: {
    AppChartStacked,
    InsightsWorkTimeExpenditureDialog,
    InsightsWorkTimeExpenditurePopover,
    InsightsNoData: () => import('~/components/insights/InsightsNoData.vue'),
  },
  mixins: [Insights, InsightsTracking],
  props: {
    maxItems: {
      type: Number,
      default: 5,
    },
  },
  data: () => ({
    widgetKey: INSIGHTS.api.workTimeExpenditure,
    limit: INSIGHTS.parameters.workTimeExpenditureLimit,
    tooltip:
      'Apps & URLs visited by your team are grouped into categories and displayed here according to their classification.',
    tooltipOptions: Object.freeze({
      placement: 'top',
    }),
    billable: false,
    title: 'Work Time Expenditure',
    showDialog: false,
  }),
  computed: {
    ...mapGetters({
      widgetData: 'workTimeExpenditure',
    }),
    ...mapState(['loadingWidgets']),
    isLoading() {
      return this.loadingWidgets[this.widgetKey];
    },
    hasData() {
      return !!(this.widgetData && this.widgetData.data && this.widgetData.data.length);
    },
    hasDataOrLoading() {
      return this.hasData || this.isLoading;
    },
    chartSections() {
      return ['core', 'non_core', 'unproductive'];
    },
    chartData() {
      return (
        this.widgetData?.data?.slice(0, this.maxItems).map((item, index) => ({
          ...item,
          // Remove when backend change the tooltip schema
          // We need this mapping for matching the new AppChartStacked schema
          ...formatFields(item),
          label: `${Math.round(item.value * 1000) / 10}% ${item.label}`,
          id: index,
        })) || []
      );
    },
    hasSelectedData() {
      return this.chartData?.length;
    },
    showMore() {
      return this.widgetData?.data?.length >= this.limit;
    },
    trackDetailsProperties() {
      return Object.freeze({
        label: 'Widget click',
        widget_name: this.title,
        action_type: 'details',
        action_label: 'View more',
        ...this.commonProps(),
      });
    },
  },
  created() {
    if (!this.hasDataOrLoading) {
      this.fetchWidgets({
        widgetKey: this.widgetKey,
        extraQueryParams: {
          limit: this.limit,
        },
      });
    }
  },
  methods: {
    ...mapActions(['fetchWidgets']),
    toggleDialog() {
      this.showDialog = !this.showDialog;
    },
  },
};
</script>
